import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { useInView } from 'react-intersection-observer'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import {
  ChatMapLiveSlotRegistry,
  CHAT_MAP_LIVE_SLOT_ROOT_MARGIN,
  CHAT_MAP_LIVE_SLOTS_DESKTOP,
  CHAT_MAP_LIVE_SLOTS_MOBILE,
} from './chatMapLiveSlotRegistry'

const ChatMapLiveSlotContext = createContext<ChatMapLiveSlotRegistry | undefined>(undefined)

interface Props {
  children: ReactNode
}

/**
 * Owns the per-chat concurrent live-Mapbox budget for in-transcript map cards.
 * Mount once around the message list (ChatContainer).
 */
function ChatMapLiveSlotProvider({ children }: Props) {
  const isMobileOrTablet = useIsMobileOrTablet()
  const maxSlots = isMobileOrTablet ? CHAT_MAP_LIVE_SLOTS_MOBILE : CHAT_MAP_LIVE_SLOTS_DESKTOP
  const [registry] = useState(() => new ChatMapLiveSlotRegistry(maxSlots))

  useEffect(() => {
    registry.setMaxSlots(maxSlots)
  }, [maxSlots, registry])

  return (
    <ChatMapLiveSlotContext.Provider value={registry}>
      {children}
    </ChatMapLiveSlotContext.Provider>
  )
}

function useChatMapLiveSlotRegistry(): ChatMapLiveSlotRegistry | undefined {
  return useContext(ChatMapLiveSlotContext)
}

/**
 * Coordinates IntersectionObserver eligibility with the chat-wide live-map cap.
 *
 * Without a provider (unit tests / isolated renders), the card shows a
 * placeholder until a ChatMapLiveSlotProvider is mounted.
 */
function useChatMapLiveSlot(slotId: string): {
  containerRef: (node?: Element | null) => void
  isLive: boolean
} {
  const registry = useChatMapLiveSlotRegistry()
  const { ref: containerRef, inView } = useInView({
    rootMargin: CHAT_MAP_LIVE_SLOT_ROOT_MARGIN,
    threshold: 0,
    // Assume on-screen until the observer reports otherwise so newly streamed
    // cards (and history cards that win the cap) do not flash a placeholder.
    initialInView: true,
  })

  // Visibility must settle before paint so isLive matches the first frame.
  useLayoutEffect(() => {
    if (!registry) return
    registry.setVisible(slotId, inView)
  }, [registry, slotId, inView])

  // Unregister only when the card unmounts or its slot id changes — not on
  // every inView flip (that would release+reclaim through unregister).
  useLayoutEffect(() => {
    if (!registry) return
    return () => {
      registry.unregister(slotId)
    }
  }, [registry, slotId])

  const subscribe = useCallback((onStoreChange: () => void) => {
    if (!registry) return () => {}
    return registry.subscribe(onStoreChange)
  }, [registry])

  const getSnapshot = useCallback(() => {
    if (!registry) return false
    return registry.isLive(slotId)
  }, [registry, slotId])

  const isLive = useSyncExternalStore(subscribe, getSnapshot, () => false)

  return { containerRef, isLive }
}

export {
  ChatMapLiveSlotProvider,
  useChatMapLiveSlot,
  useChatMapLiveSlotRegistry,
}
